import {
  BASE_URL,
  getAuthHeaders,
  isSessionTokenExpired,
  handleSessionExpiration,
  briefHttpError,
  ApiEnvelopeError,
} from "./apiHelpers";
import axios from "axios";

export interface RequestOptions {
  /** Extra headers merged over the defaults. */
  headers?: Record<string, string>;
  /** AbortSignal for cancellation. */
  signal?: AbortSignal;
  /** Override the base URL (rarely needed — leave undefined for /api/v1). */
  baseUrl?: string;
}

export interface SSEMessage {
  event?: string;
  id?: string;
  data: string;
}

export interface SSEOptions {
  onMessage: (msg: SSEMessage) => void;
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

/** Core fetch request with auth, JSON serialization, and error mapping. */
async function request<T>(
  path: string,
  init: RequestInit & { baseUrl?: string },
): Promise<T> {
  const baseUrl = init.baseUrl ?? BASE_URL;
  const { headers: extraHeaders, baseUrl: _drop, ...rest } = init;
  void _drop;

  // Browser sets its own Content-Type (multipart boundary) for these body types.
  const body = rest.body;
  const skipJsonHeader =
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    body instanceof URLSearchParams;

  const defaultHeaders: Record<string, string> = skipJsonHeader
    ? {}
    : { "Content-Type": "application/json" };

  const mergedHeaders: Record<string, string> = {
    ...defaultHeaders,
    ...getAuthHeaders(),
    ...((extraHeaders as Record<string, string> | undefined) ?? {}),
  };

  const res = await fetch(`${baseUrl}${path}`, { ...rest, credentials: "include", headers: mergedHeaders });

  if (res.status === 401 && isSessionTokenExpired()) {
    handleSessionExpiration();
    const err = new Error("Session expired. Please login again.");
    err.name = "Session Expired";
    throw err;
  }

  if (!res.ok) {
    const bodyText = await res.text().catch(() => "");
    let title = res.status === 401 ? "Access Denied" : "Request Failed";
    let detail = briefHttpError(res.status, bodyText, res.statusText);
    let errorCode: string | undefined;
    try {
      const parsed = JSON.parse(bodyText);
      const firstError = parsed?.errors?.[0] || parsed;
      if (firstError?.title || firstError?.detail) {
        title = firstError.title || title;
        errorCode = firstError.code;
        detail = firstError.detail || firstError.message || detail;
      }
    } catch { /* non-JSON body */ }
    const err = new Error(detail);
    err.name = title;
    (err as any).errorCode = errorCode;
    (err as any).status = res.status;
    throw err;
  }

  if (res.status === 204 || res.headers.get("content-length") === "0") {
    return undefined as T;
  }
  return res.json() as Promise<T>;
}

/** JSON-stringify plain objects; pass through FormData/Blob/string/undefined. */
function serializeBody(body: unknown): BodyInit | undefined {
  if (body == null) return undefined;
  if (
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    body instanceof URLSearchParams ||
    typeof body === "string"
  ) {
    return body as BodyInit;
  }
  return JSON.stringify(body);
}

export const apiClient = {
  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>(path, {
      method: "GET",
      signal: options?.signal,
      headers: options?.headers,
      baseUrl: options?.baseUrl,
    });
  },
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>(path, {
      method: "POST",
      body: serializeBody(body),
      signal: options?.signal,
      headers: options?.headers,
      baseUrl: options?.baseUrl,
    });
  },
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>(path, {
      method: "PUT",
      body: serializeBody(body),
      signal: options?.signal,
      headers: options?.headers,
      baseUrl: options?.baseUrl,
    });
  },
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>(path, {
      method: "PATCH",
      body: serializeBody(body),
      signal: options?.signal,
      headers: options?.headers,
      baseUrl: options?.baseUrl,
    });
  },
  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>(path, {
      method: "DELETE",
      signal: options?.signal,
      headers: options?.headers,
      baseUrl: options?.baseUrl,
    });
  },
  /** Upload FormData. `envelopeError` wraps failures in ApiEnvelopeError. */
  async upload<T>(
    path: string,
    formData: FormData,
    options?: RequestOptions & { envelopeError?: boolean },
  ): Promise<T> {
    if (!options?.envelopeError) {
      return request<T>(path, {
        method: "POST",
        body: formData,
        signal: options?.signal,
        headers: options?.headers,
        baseUrl: options?.baseUrl,
      });
    }
    // Bypass generic handler so the store can render envelope-error UI.
    const baseUrl = options?.baseUrl ?? BASE_URL;
    const res = await fetch(`${baseUrl}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { ...getAuthHeaders(), ...(options?.headers ?? {}) },
      body: formData,
      signal: options?.signal,
    });
    const text = await res.text();
    let parsed: any = null;
    try { parsed = text ? JSON.parse(text) : null; } catch { /* non-JSON */ }
    if (!res.ok) {
      const e = parsed?.errors?.[0] ?? {};
      throw new ApiEnvelopeError({
        code: e.code ?? "UNKNOWN",
        title: e.title ?? "Upload failed",
        detail: e.detail ?? briefHttpError(res.status, text, res.statusText),
        pointer: e.pointer,
        providerMessage: e?.details?.providerMessage,
        status: res.status,
      });
    }
    return parsed as T;
  },
  /** Build a full URL for <a href> / <iframe src>. Does NOT fetch. */
  buildUrl(path: string): string {
    return `${BASE_URL}${path}`;
  },
  /** Subscribe to a Server-Sent Events stream (GET or POST). */
  async subscribeSSE(path: string, opts: SSEOptions): Promise<void> {
    const isJsonBody =
      opts.body != null &&
      typeof opts.body === "object" &&
      !(opts.body instanceof FormData) &&
      !(opts.body instanceof Blob) &&
      !(opts.body instanceof ArrayBuffer) &&
      !(opts.body instanceof URLSearchParams);

    const res = await fetch(`${BASE_URL}${path}`, {
      method: opts.method ?? "GET",
      credentials: "include",
      headers: {
        ...getAuthHeaders(),
        Accept: "text/event-stream",
        ...(isJsonBody ? { "Content-Type": "application/json" } : {}),
      },
      body:
        opts.body == null
          ? undefined
          : isJsonBody
            ? JSON.stringify(opts.body)
            : (opts.body as BodyInit),
      signal: opts.signal,
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => "");
      throw new Error(briefHttpError(res.status, detail, res.statusText));
    }
    if (!res.body) return;

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    const emit = (frame: string) => {
      const fields = frame.split(/\r?\n/);
      let event: string | undefined;
      let id: string | undefined;
      const data: string[] = [];
      for (const line of fields) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("id:")) id = line.slice(3).trim();
        else if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
      }
      if (data.length > 0) opts.onMessage({ event, id, data: data.join("\n") });
    };

    const abortHandler = () => {
      void reader.cancel().catch(() => undefined);
    };
    opts.signal?.addEventListener("abort", abortHandler, { once: true });

    try {
      while (true) {
        const { done, value } = await reader.read();
        buffer += decoder.decode(value, { stream: !done });
        const frames = buffer.split(/\r?\n\r?\n/);
        buffer = frames.pop() ?? "";
        for (const frame of frames) {
          emit(frame);
        }

        if (done) {
          if (buffer.trim()) emit(buffer);
          return;
        }
      }
    } finally {
      opts.signal?.removeEventListener("abort", abortHandler);
    }
  },
};

/** Legacy axios GET (goes through offline interceptor). Prefer apiClient.get. */
export const getAPI = async (apiUrl: string, timeoutMs?: number) => {
  try {
    const config =
      timeoutMs === 0
        ? { timeout: 0, noTimeout: true }
        : timeoutMs
          ? { timeout: timeoutMs }
          : undefined;
    const response = await axios.get(apiUrl, config);
    return response.data;
  } catch (error: any) {
    if (error?.isOffline) throw new Error("OFFLINE");
    throw error;
  }
};
